import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { appConfig } from '~/app.config';
import { TipsVariantEnum } from '~/component/tips/enum/variant.enum';
import { SuccessStatusEnum } from '~/core/enums/success-status.enum';
import { Activity } from '~/core/model/activity.model';
import { repeatUntil } from '~/core/operators/repeat-until.operator';
import { FormControlService } from '~/core/services/form/form-control.service';
import { noHtmlTagValidator } from '~/core/validators/no-html-tag/no-html-tag.validator';
import { OrganizerSharedLayoutEditFieldComponent } from '~/organizer/shared/components/shared/layouts/edit-field/edit-field.component';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerSharedLocatableService } from '~/organizer/shared/services/locatable/locatable.service';

@Component({
  selector: 'app-organizer-activity-edition-title',
  templateUrl: './title.component.html',
})
export class OrganizerActivityEditionTitleComponent
  implements OnInit, OnDestroy
{
  @ViewChild(OrganizerSharedLayoutEditFieldComponent)
  public layout: OrganizerSharedLayoutEditFieldComponent;

  private readonly subscriptions = new Subscription();

  public readonly defaultMaximumCharacters =
    appConfig.formValidationRules.activityTitleMaximumLength;
  public readonly variantTips = TipsVariantEnum.INFO;

  public activity: Activity;
  public isFormValid: boolean;
  public isOverlayDisplayed = false;
  public overlayStatus = SuccessStatusEnum.LOADING;
  public title: FormControl;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly locatableService: OrganizerSharedLocatableService,
    private readonly organizerSharedActivityService: OrganizerSharedActivityService,
    private readonly formControlService: FormControlService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.title = new FormControl('', [
      Validators.required,
      Validators.maxLength(this.defaultMaximumCharacters),
      noHtmlTagValidator(),
    ]);

    this.subscriptions.add(
      this.title.valueChanges.subscribe(() => {
        this.isFormValid = this.title.valid && this.title.dirty;
      })
    );

    this.subscriptions.add(
      this.activatedRoute.data
        .pipe(
          mergeMap(({ activityData }) => {
            return this.organizerSharedActivityService.getActivity(
              activityData.activity?.sku
            );
          })
        )
        .subscribe((activity) => {
          this.activity = activity;
          this.title.setValue(activity.name);
        })
    );
  }

  public get currentCharacters(): number {
    return this.title.value?.length || 0;
  }

  public get hasInitialValue(): boolean {
    return Boolean(this.activity?.name);
  }

  public errorMessages(inputName: string): string[] {
    return this.formControlService.getErrorMessages(this.title, inputName);
  }

  public updateTitle(): void {
    this.isOverlayDisplayed = true;

    this.organizerSharedActivityService
      .patchActivityById(this.activity.sku, {
        name: [
          this.locatableService.buildCurrentLocatableBody(this.title.value),
        ],
      })
      .pipe(
        mergeMap(() => {
          return this.organizerSharedActivityService
            .getActivity(this.activity?.sku.toString())
            .pipe(
              repeatUntil((activity) => activity.name === this.title.value)
            );
        })
      )
      .subscribe(() => {
        this.overlayStatus = SuccessStatusEnum.SUCCESS;
      });
  }
}
